import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {useAuth} from 'app/modules/auth'
import * as Yup from 'yup'
import {FC, useEffect, useState} from 'react'
import {useFormik} from 'formik'
import {ControlDataType} from '../Controls'
import Input from 'app/components/Input'
import Textarea from 'app/components/Textarea'
import {doc, addDoc, updateDoc, collection, serverTimestamp} from '@firebase/firestore'
import db from 'services/firebase'
import {showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  customControl: ControlDataType
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Title is required'),
  description: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(800, 'Maximum 800 characters')
    .required('Description is required'),
})

const AddEditCustomControlModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  isEditMode,
  customControl,
}) => {
  const {currentUser} = useAuth()

  const [loading, setLoading] = useState(false)

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<ControlDataType>({
    initialValues: {id: '', title: '', description: '', category: ''},
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)

        const tenantId = currentUser?.tenantId || ''
        const controlsRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'controls',
          'all_controls'
        )

        if (!isEditMode) {
          const newDocRef = await addDoc(controlsRef, {
            title: values.title,
            description: values.description,
            category: 'Custom',
            isCustom: true,
            isShownInTrustCenter: true,
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${values.title} custom control for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Custom control added',
          })

          showSuccessToast({message: 'Custom Control has been added successfully'})
        } else {
          await updateDoc(doc(controlsRef, customControl.id), {
            title: values.title,
            description: values.description,
          })
          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${customControl.title} custom control for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Custom control updated',
          })
          showSuccessToast({message: 'Custom Control has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating custom control for trust center')
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (customControl.title) {
      formik.setValues({
        id: customControl.id,
        title: customControl.title,
        description: customControl.description,
        category: customControl.category,
        isShownInTrustCenter: customControl.isShownInTrustCenter,
        isCustom: customControl.isCustom,
      })
    }
  }, [customControl])

  const okBtnClickHandler = async () => {
    formik.submitForm()
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit Custom Control' : 'Add Custom Control'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add Custom Control'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding Custom Control...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Input
          type='text'
          label='Title'
          placeholder='Control Title'
          error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
          {...formik.getFieldProps('title')}
        />

        <Textarea
          label='Description'
          placeholder='Type Control Description...'
          error={
            formik.touched.description && formik.errors.description ? formik.errors.description : ''
          }
          {...formik.getFieldProps('description')}
        />
      </div>
    </CustomModal>
  )
}

export default AddEditCustomControlModal
